import { useCallback } from "react";
import type { Certification } from "../types";

/**
 * Custom hook for handling selection events and interactions
 * Provides specific handlers for badge and certification tile interactions
 */
export const useSelectionHandlers = () => {
  // Badge interaction handlers (for SustainabilityBadge)
  const createBadgeHandlers = useCallback((onBadgeClick: () => void) => {
    const handleBadgeClick = () => {
      onBadgeClick();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleBadgeClick();
      }
    };

    return {
      handleBadgeClick,
      handleKeyDown,
      // Combined props for easy spreading
      badgeProps: {
        onClick: handleBadgeClick,
        onKeyDown: handleKeyDown,
      },
    };
  }, []);

  // Certification tile handlers (for FeatureCard)
  const createCertificationTileHandlers = useCallback(
    (
      certification: Certification,
      onCertificationSelect?: (
        cert: Certification,
        element?: HTMLElement,
      ) => void,
    ) => {
      const handleCertificationClick = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.currentTarget;

        if (onCertificationSelect && target instanceof HTMLElement) {
          onCertificationSelect(certification, target);
        }
      };

      const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const target = e.currentTarget;

          if (onCertificationSelect && target instanceof HTMLElement) {
            onCertificationSelect(certification, target);
          }
        }
      };

      return {
        handleCertificationClick,
        handleKeyDown,
        // Combined props for easy spreading
        tileProps: {
          onClick: handleCertificationClick,
          onKeyDown: handleKeyDown,
          role: "button" as const,
          tabIndex: 0,
          "aria-label": `Learn more about ${certification.displayName}`,
        },
      };
    },
    [],
  );

  return {
    // Specific component handlers
    createBadgeHandlers,
    createCertificationTileHandlers,
  };
};
